import PropTypes from 'prop-types';
import React from 'react';
import FontIcon from 'react-toolbox/lib/font_icon';
import styles from 'flagList.scss';
import S from 'styles/form.scss';
import cx from 'classnames';
import MediaQuery from 'react-responsive';
import RemoveFlagModal from './removeFlagModal';
import ViewFlagModal from './viewFlagModal';
import VerifyBuildDateModal from './verifyBuildDateModal';
import ReactTooltip from 'react-tooltip';
import { getMinLocalDate } from '../../../../../utils/dateUtils';
import { isActionable, isCssFlag } from '../../../../../utils/flagUtils';
import { HOLIDAY_PROMO_FLAG, VERIFY_BUILD_DATE_FLAG, CLAIM_ECR_ALLOWED_POSITIONS } from 'config/constants';
import { connect } from 'react-redux';
import TooltipStyles from 'styles/tooltip.scss';

const DEFAULT_STATE = { activeModal: null, activeFlag: null };

const MINIMUM_WIDTH = 450;

const flagTooltipText = flag => `${flag.suboption && `${flag.suboption} - `}${flag.note}`;

const positionMatches = (a, b) => (a || '').toLowerCase() === (b || '').toLowerCase();

/** Applying css classes based on the flag name. */
const changeToClassName = str => `flag${str.replace(/\s/g, '')}`;

class FlagList extends React.Component {
  constructor(props) {
    super(props);
    this.handleClose = this.handleClose.bind(this);
    this.renderRemoveFlagModal = this.renderRemoveFlagModal.bind(this);
    this.state = DEFAULT_STATE;
  }

  handleClose = () => this.setState({ activeModal: null });

  handleClick = (flag, action) => {
    if (action === 'view' && flag.name === 'Welcome Call Needed' && this.props.onFlagNavigate) {
      this.props.onFlagNavigate(flag);
      return;
    }
    if (action === 'view' && flag.name === VERIFY_BUILD_DATE_FLAG) {
      this.setState({ activeFlag: flag, activeModal: 'verifyBuildDate' });
      return;
    }
    // Flag text is not clickable if not in mobile view
    if (action === 'view' && window.outerWidth >= MINIMUM_WIDTH) return;
    this.setState({ activeFlag: flag, activeModal: action });
  }

  isValidPosition = (flagName, position, role, name) => {
    let isValid = true;
    if (role !== 'admin') {
      if (flagName === 'IME') return false;
      if (flagName === 'Welcome Call Needed') return false;
      // Workrooms own Holiday Promo resolution; CCS cannot remove it
      if (flagName === HOLIDAY_PROMO_FLAG && positionMatches(position, 'Customer Care Specialist')) return false;
      if (flagName === 'Escalation') {
        isValid = positionMatches(position, 'Customer Care Specialist') || ['Fatima Singletary', 'Kyle Reece', 'Jimmy Chestnut'].includes(name);
      } else if (['ECR', 'Claim'].includes(flagName)) {
        isValid = CLAIM_ECR_ALLOWED_POSITIONS.some(p => positionMatches(p, position)) || ['Fatima Singletary', 'Kyle Reece', 'Jimmy Chestnut'].includes(name);
      }
    }
    return isValid;
  };

  renderRemoveFlagModal = () => this.setState({ activeModal: 'remove' });

  render = () => {
    const {
      flags,
      deleteEnabled,
      label,
      tooltipEnabled,
      useHighContrast,
      position,
      role,
      name,
    } = this.props;
    return (
      <div>
        {label && <h6 className={cx(S.label, styles.flaglabel)}>{label}</h6>}
        <div className={styles.flagcontainer}>
          {flags.map((flag) => {
            /** Classes in the scss defined by the flag name.*/
            const flagClass = changeToClassName(flag.name);
            /**
             * Might want to change this to a switch case with some ternary
             * conditional for each case.
             */
            let tooltipStyle;
            if (isActionable(flag) && useHighContrast) tooltipStyle = 'tooltip-high-contrast-actionable';
            else if (useHighContrast && isActionable(flag) === false) tooltipStyle = 'tooltip-high-contrast-information';
            else tooltipStyle = '';
            return (<div
              key={flag.id}
              className={cx({
                [styles.actionable]: isActionable(flag),
                [styles[flagClass]]: isCssFlag(flag) || flag.name === 'Pro Direct',
                [styles.priorityCustomer]:
                  flag.name === 'Priority Customer',
              }, styles.flag)}
            >
              <button
                className={cx(styles.flagtext, {
                  [styles.navigable]:
                    (flag.name === 'Welcome Call Needed' && this.props.onFlagNavigate) ||
                    flag.name === VERIFY_BUILD_DATE_FLAG,
                })}
                data-tip
                data-for={`flag${flag.id}`}
                onClick={() => this.handleClick(flag, 'view')}
              >
                {flag.due_at && (<FontIcon
                  className="notification"
                  value="notifications_active"
                />)}
                {flag.name}
              </button>
              {tooltipEnabled && (<MediaQuery minWidth={MINIMUM_WIDTH}>
                <ReactTooltip
                  id={`flag${flag.id}`}
                  className={TooltipStyles[tooltipStyle]}
                  place="top"
                  effect="solid"
                >
                  <span className={styles.tooltipFlagNote}>
                    {flagTooltipText(flag)}
                  </span>
                  <p>{`${flag.user_name} on ${getMinLocalDate(
                    flag.created_at,
                  )}`}</p>
                </ReactTooltip>
              </MediaQuery>)}
              {deleteEnabled && this.isValidPosition(flag.name, position, role, name) && (<FontIcon
                value="close"
                className={styles.close}
                onClick={() => this.handleClick(flag, 'remove')}
              />)}
            </div>
            );
          })}
        </div>
        <RemoveFlagModal
          active={this.state.activeModal === 'remove'}
          onClose={this.handleClose}
          flag={this.state.activeFlag}
        />
        <ViewFlagModal
          active={this.state.activeModal === 'view'}
          onClose={this.handleClose}
          onRemove={this.renderRemoveFlagModal}
          flag={this.state.activeFlag}
          flagTooltipText={flagTooltipText}
          deleteEnabled={deleteEnabled && (!this.state.activeFlag ||
            this.isValidPosition(this.state.activeFlag.name, position, role, name))}
        />
        <VerifyBuildDateModal
          active={this.state.activeModal === 'verifyBuildDate'}
          onClose={this.handleClose}
          flag={this.state.activeFlag}
        />
      </div>
    );
  }
}

FlagList.propTypes = {
  flags: PropTypes.array.isRequired,
  tooltipEnabled: PropTypes.bool.isRequired,
  deleteEnabled: PropTypes.bool.isRequired,
  label: PropTypes.string,
  useHighContrast: PropTypes.bool,
  role: PropTypes.string,
  position: PropTypes.string,
  name: PropTypes.string,
  onFlagNavigate: PropTypes.func,
};

FlagList.defaultProps = {
  flags: [],
  deleteEnabled: false,
  tooltipEnabled: true,
};

const mapStateToProps = (state) => {
  const { settings: { useHighContrast }, user: { role, position, name } } = state;
  return { useHighContrast, role, position, name };
};

export default connect(mapStateToProps)(FlagList);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/components/flagList.jsx